import React, { useState, useMemo } from 'react';
import { useQuote } from '../../contexts/QuoteContext';
import type { Color, ColorOption } from '../../types/quote';
import productConfig from '../../data/productConfig.json';

interface Step5ColorProps {
  isActive: boolean;
  isCompleted: boolean;
  isDisabled: boolean;
}

const Step5Color: React.FC<Step5ColorProps> = ({ isActive, isCompleted, isDisabled }) => {
  const { quoteState, updateQuoteState } = useQuote();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomColor, setSelectedCustomColor] = useState<ColorOption | null>(null);

  // Helper function to get excluded colors for the current variant
  const getExcludedColors = (): string[] => {
    if (!quoteState.selectedProduct || !quoteState.selectedModel || !quoteState.selectedVariant) {
      return [];
    }

    try {
      const productType = productConfig.productTypes[quoteState.selectedProduct as keyof typeof productConfig.productTypes];
      if (!productType) return [];

      const model = productType.models[quoteState.selectedModel.id as keyof typeof productType.models];
      if (!model) return [];

      const variant = (model as any).variants[quoteState.selectedVariant.id];
      if (!variant) return [];

      return variant.excludeColors || [];
    } catch (error) {
      console.warn('Error getting excluded colors:', error);
      return [];
    }
  };

  // Get base colors from colorDefinitions (available for all products)
  const baseColors = useMemo(() => {
    const baseColorIds = ['9003', '7040', '7024', '9005']; // white, light grey, graphite grey, jet black

    // Get excluded colors for the current variant
    const excludedColors = getExcludedColors();

    // Filter out excluded colors
    const availableColorIds = baseColorIds.filter(colorId => !excludedColors.includes(colorId));

    return availableColorIds.map(colorId => {
      const colorDef = productConfig.colorDefinitions[colorId as keyof typeof productConfig.colorDefinitions] as any;
      return {
        id: colorId,
        name: colorDef.name,
        hexCode: colorDef.hexCode,
        rgb: colorDef.rgb
      };
    });
  }, [quoteState.selectedProduct, quoteState.selectedModel, quoteState.selectedVariant]);

  // Clear selected color if it becomes excluded when variant changes
  React.useEffect(() => {
    if (quoteState.selectedColor) {
      const excludedColors = getExcludedColors();
      if (excludedColors.includes(quoteState.selectedColor.id)) {
        updateQuoteState({ selectedColor: null });
        setSelectedCustomColor(null);
      }
    }
  }, [quoteState.selectedProduct, quoteState.selectedModel, quoteState.selectedVariant]);

  // Get custom colors (only available for trailers)
  const customColors = useMemo(() => {
    if (quoteState.selectedProduct !== 'trailer') return [];

    const customColorOptions = productConfig.colorDefinitions.custom?.options || {};
    return Object.values(customColorOptions);
  }, [quoteState.selectedProduct]);

  // Filter custom colors based on search term
  const filteredCustomColors = useMemo(() => {
    if (!searchTerm.trim()) return customColors;

    const searchLower = searchTerm.toLowerCase();
    return customColors.filter(color =>
      color.name.toLowerCase().includes(searchLower) ||
      color.id.toLowerCase().includes(searchLower)
    );
  }, [customColors, searchTerm]);

  const handleBaseColorSelect = (color: Color) => {
    if (isDisabled) return;
    setSelectedCustomColor(null);
    updateQuoteState({ selectedColor: color });
  };

  const handleCustomColorSelect = (colorOption: ColorOption) => {
    if (isDisabled) return;
    setSelectedCustomColor(colorOption);

    // Create a color object with the selected custom option
    const customColor: Color = {
      id: colorOption.id,
      name: colorOption.name,
      hexCode: colorOption.hexCode,
      rgb: colorOption.rgb,
      options: {
        [colorOption.id]: colorOption
      }
    };

    updateQuoteState({ selectedColor: customColor });
  };

  const isBaseColorSelected = (colorId: string) => {
    return quoteState.selectedColor?.id === colorId;
  };

  const isCustomColorSelected = (colorOption: ColorOption) => {
    return selectedCustomColor?.id === colorOption.id;
  };

  return (
    <div className={`step-section ${isActive ? 'active' : isDisabled ? 'disabled' : 'inactive'} ${!isActive && !isCompleted ? 'collapsed' : ''}`}>
      <div className="step-header">
        <div className={`step-number ${isCompleted ? 'completed' : isActive ? 'active' : 'disabled'}`}>
          <span>5</span>
        </div>
        <h2 className="step-title">Select Colour</h2>
      </div>
      {(isActive || isCompleted) && (
        <div className="color-selection-container">
          {/* Stock Colors Section */}
          <div className="stock-colors-section">
            <h3 className="section-title">Choose from stock colours</h3>
            <div className="stock-colors-grid">
              {baseColors.map((color) => (
                <div
                  key={color.id}
                  className={`color-swatch ${isBaseColorSelected(color.id) ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => handleBaseColorSelect(color)}
                  title={color.name}
                  style={{
                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                    backgroundColor: `rgb(${color.rgb.join(', ')})`
                  }}
                >
                  <span className="color-code">{color.id}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Custom Colors Section */}
          {quoteState.selectedProduct === 'trailer' && (
            <div className="custom-colors-section">
              <div className="custom-colors-header">
                <h3 className="section-title">Choose from {customColors.length} custom colours (special order)</h3>
                <div className="search-container">
                  <input
                    type="text"
                    placeholder="Search colours..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="color-search-input"
                    disabled={isDisabled}
                  />
                  <div className="search-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                  </div>
                </div>
              </div>
              <div className="custom-colors-grid">
                {filteredCustomColors.map((colorOption) => (
                  <div
                    key={colorOption.id}
                    className={`color-swatch ${isCustomColorSelected(colorOption) ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                    onClick={() => handleCustomColorSelect(colorOption)}
                    title={colorOption.name}
                    style={{
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      backgroundColor: `rgb(${colorOption.rgb.join(', ')})`
                    }}
                  >
                    <span className="color-code">{colorOption.id}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Step5Color;
